/** 发布弹窗及发布确认 */
import React, { useCallback, useEffect, useMemo, useState, memo, useRef } from 'react';
import { useRouter } from 'next/router';
import PublishModal from '@tencent/tpm-meeting-active/lib/PublishModal';
import { publishActivity } from '@model/activity';
import { useActivityInfo } from 'src/hooks/useActivityInfo';
import { useStore, ACTION } from '@utils/event-store/useEventData';
import dayjs from 'dayjs';
import { message, Alert, Button } from '@tencent/tea-component';
import PublishLoading from '@component/PublishLoading';
import bgImg from './img/bg.jpeg';
import '@tencent/tpm-meeting-active/lib/PublishModal/style';
import './Publish.scss';
import dataReport from '@utils/data-report';
import ShareActivityModal from 'src/components/ShareActiveModal';
import COMPLETE_URL from 'src/assets/imgs/complete.svg';
import GradientDescDialogTable from 'src/pages/event/[eventid]/venue/components/gradientDescDialog';
import { openPcLobbyPage } from 'src/utils/concat-meeting-util';
import { useTranslation } from 'react-i18next';
import { updateRegisterDetail } from '@model/event-register';
import { useHostRegistrationUrl } from '@hooks/useRainbowConfig';
import { openNewPage } from '@utils/open-new-pgae';

const Publish = () => {
  const [shareModal, setShareModal] = useState({ show: false, success: 0 });
  const [tableVisible, setTableVisible] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();
  const { query } = router;
  const eventId = String(query.eventid);
  const { activityInfo, mutate: updateInfo } = useActivityInfo({ event_id: eventId });
  const registrationUrl = useHostRegistrationUrl(activityInfo?.host_id);
  const {
    errorInfo,
    warnItems,
    handleError,
    eventInfo,
    dispatch,
    showPublishDialog,
    loadingVisible,
    publishedEventInfo,
    errorItemsSort,
  } = useStore();

  const ref = useRef<any>(null);
  ref.current = { ...eventInfo, shareModal };
  const isNoLimit = activityInfo?.access_rule === 1;
  const ticketList = activityInfo?.ticket_info?.ticket_group_info;

  // 需要人工审核，但是不在广场上，轮询人工审核状态
  const pollingAuditStatus = (index?: number) => {
    const {
      is_person_audit: person,
      plaza_status: plaza,
      audit_status: audit,
      shareModal,
    } = ref.current || {};
    if (activityInfo?.plaza_status === 0 && plaza === 1 && shareModal.show) {
      setShareModal({ show: true, success: 2 });
    }
    if (person === 1 && plaza === 0 && shareModal.show) {
      // 忽略第一次，因为此时后台异步修改 audit_status 的值
      if (audit === 0 || index === -1) {
        dispatch({ type: ACTION.UPDATEINFOREEDIRTSTATUS, data: {} });
        setTimeout(() => {
          pollingAuditStatus();
        }, 60000);
        return;
      }
      if (audit === -1) {
        setShareModal({ show: false, success: 0 });
        dispatch({ type: ACTION.GETERRORLIST });
      }
    }
  };

  // 轮询报错中心接口
  const pollingErrorCenter = (i = 0) => {
    if (i >= 4) return;
    dispatch({ type: ACTION.GETERRORLIST });
    setTimeout(() => {
      pollingErrorCenter(i + 1);
    }, 2000);
  };

  const setLoadingVisible = useCallback((status: boolean) => {
    dispatch({ type: ACTION.UPDATEDATA, data: { loadingVisible: status } });
  }, []);

  const close = useCallback(() => {
    dispatch({ type: ACTION.UPDATEDATA, data: { showPublishDialog: false } });
  }, []);

  const activityStatus = useMemo(() => {
    if (activityInfo?.is_public === 1) return { text: '公开发布', type: 'public' };
    return { text: '私密发布', type: 'private' };
  }, [activityInfo?.is_public]);

  const lowestPrice = useMemo(() => {
    const min: any = activityInfo?.ticket_info?.min_fee;
    const max = activityInfo?.ticket_info?.max_fee;
    if (!min && !max) return '免费';
    if (min === max || !max) return `¥${(min / 100).toFixed(2)}`;
    return `¥${(min / 100).toFixed(2)}-¥${(max / 100).toFixed(2)}`;
  }, [activityInfo?.ticket_info]);

  const activityTime = useMemo(() => {
    const startTime = dayjs((activityInfo?.start_time || 0) * 1000);
    const endTime = dayjs((activityInfo?.end_time || 0) * 1000);
    let endTimeFmt = 'MM月DD日 HH:mm';
    if (endTime.date() === startTime.date()) {
      endTimeFmt = 'HH:mm';
    }
    return `${startTime.format('MM月DD日 HH:mm')} - ${endTime.format(endTimeFmt)}`;
  }, [activityInfo?.start_time, activityInfo?.end_time]);

  const venue = useMemo(() => {
    if (Number(activityInfo?.scheduler_info?.total) === 0)
      return {
        large: 0,
        small: 0,
        brand: 0,
        largeText: t('basic.liveHall'),
        smallText: t('basic.sessions'),
        largeIcon: <i className={`iconfont icon-${t('tickets.venueIcon1')}`}></i>,
        smallIcon: <i className={`iconfont icon-${t('tickets.venueIcon2')}`}></i>,
      };
    return activityInfo?.scheduler_info?.type_count.reduce(
      (prev, cur) => ({
        [['large', 'small', 'brand'][cur.venue_type - 1]]: cur.count,
        ...prev,
      }),
      {
        largeText: t('basic.liveHall'),
        smallText: t('basic.sessions'),
        largeIcon: <i className={`iconfont icon-${t('tickets.venueIcon1')}`}></i>,
        smallIcon: <i className={`iconfont icon-${t('tickets.venueIcon2')}`}></i>,
      },
    );
  }, [activityInfo?.scheduler_info]);

  const tickets = useMemo(() => {
    const ticketMap = [
      {
        label: '隐藏门票',
        tickets: [] as any,
      },
      {
        label: '公开门票',
        tickets: [] as any,
      },
    ];

    ticketList?.forEach((t: any) => {
      if (t?.status > -1) {
        ticketMap[t?.status]?.tickets.push({
          name: t.ticket_group_name,
          price: !t?.discount_price ? '免费' : `¥${(t.discount_price / 100).toFixed(2)}`,
          sum: t.stock_num,
        });
      }
    });
    return ticketMap.filter((item) => item?.tickets.length > 0).reverse();
  }, [ticketList]);

  const participants = useMemo(() => {
    const info = activityInfo?.people_info;
    return [
      {
        type: '嘉宾',
        sum: info?.guest_count || 0,
      },
      {
        type: '主持人',
        sum: info?.host_count || 0,
      },
      {
        type: '观众',
        sum: info?.audience_count || 0,
      },
    ];
  }, [activityInfo?.people_info]);
  // 更新缓存activityInfo 状态
  const updateBaseInfo = () => {
    let status = 4;
    if (activityInfo?.status === 0 || activityInfo?.status === 3) {
      status = 2;
    }
    dispatch({
      type: ACTION.UPDATEDATA,
      data: {
        eventInfo: {
          ...eventInfo,
          status,
        },
      },
    });
    updateInfo(
      {
        ...activityInfo,
        status,
      },
      {
        revalidate: false, // 取消请求远程数据
      },
    );
  };

  const doPublish = useCallback(() => {
    if (errorInfo.length > 0) return;
    close?.();
    updateBaseInfo();
    if (
      (eventInfo?.is_person_audit === 1 && eventInfo?.plaza_status === 0) ||
      eventInfo?.is_person_audit === 2
    ) {
      setShareModal({ show: true, success: 0 });
    } else {
      setLoadingVisible(true);
    }
    publishActivity({ event_id: eventId })
      .then(() => {
        dispatch({ type: ACTION.UPDATEINFOREEDIRTSTATUS, data: {} });
      })
      .catch((err) => {
        message.error({ content: err });
        dispatch({
          type: ACTION.UPDATEINFOREEDIRTSTATUS,
          data: 1,
        });
        setLoadingVisible(false);
        setShareModal({ show: false, success: 0 });
      });
  }, [updateBaseInfo, eventInfo, errorInfo]);

  const publishBtn = [
    {
      type: 'weak',
      text: '返回编辑',
      onClick: () => {
        dataReport('e#event_publish#back_button#click');
        close();
      },
    },
    {
      type: 'primary',
      text: '确认发布',
      disabled: errorInfo.length > 0,
      onClick: () => {
        dataReport('e#event_publish#publish_button#click');
        doPublish();
      },
    },
  ];

  const toPreview = useCallback(() => {
    dataReport('e#event_publish#preview_button#click');
    const previewUrl = `${registrationUrl}?isPreview=1`.replace('[eventId]', eventId);
    openNewPage(previewUrl);
  }, [eventId, registrationUrl]);

  const alertInfo = useMemo(() => {
    if (!errorInfo?.length && !warnItems.length) return <></>;
    let errorText = errorItemsSort !== 2 ? '审核不通过' : '发布失败';
    if (eventInfo?.status === 1 && errorText === '审核不通过') {
      errorText = '上架广场审核未通过';
    }
    const arr = errorInfo.length > 2 ? errorInfo.slice(0, 2) : errorInfo;
    return (
      <>
        {errorInfo.length > 2 && (
          <Alert
            type="error"
            extra={
              <Button
                type="link"
                className="publish-err-btn size-xs"
                onClick={() => {
                  close?.();
                  handleError('-1');
                }}
              >
                查看全部
              </Button>
            }
          >
            <div className="publish-err-txt">
              {errorText}，<span className="publish-err-number">{errorInfo.length}</span> 项待处理
            </div>
          </Alert>
        )}
        {arr.map((item, index) => {
          const txt = item?.sort === 3 ? '知道了' : '处理';
          return (
            <Alert
              key={index}
              type="error"
              extra={
                <Button
                  type="link"
                  className="publish-err-btn size-xs"
                  onClick={() => {
                    close();
                    handleError(item?.error_type, index);
                  }}
                >
                  {item?.error_type === '6' ? '前往验证' : txt}
                </Button>
              }
            >
              {item.msg || ''}
            </Alert>
          );
        })}
        {warnItems.map((item, index) => (
          <Alert
            key={index}
            type="warning"
            extra={
              <Button
                type="link"
                className="publish-warn-btn size-xs"
                onClick={() => {
                  close();
                  setTableVisible(true);
                }}
              >
                {'查看详情'}
              </Button>
            }
          >
            {item.msg || ''}
          </Alert>
        ))}
      </>
    );
  }, [errorInfo, warnItems, eventInfo?.status, errorItemsSort]);
  useEffect(() => {
    // 当前状态从发布中变成发布成功
    // 从发布修改中变成发布成功
    if (
      eventInfo?.status === 1 &&
      eventInfo?.reedit_status === 0 &&
      (activityInfo?.status === 2 || activityInfo?.status === 4)
    ) {
      updateRegisterDetail(eventInfo.event_id).then(() => {
        setLoadingVisible(false);
        setShareModal({ show: true, success: 1 });
        pollingAuditStatus(-1);
      });
    }
    // 当前状态从发布中变成发布失败
    // 当前状态从发布修改中变成修改失败
    if (
      (eventInfo?.status === 3 && activityInfo?.status === 2) ||
      (eventInfo?.status === 5 && activityInfo?.status === 4)
    ) {
      setLoadingVisible(false);
      setShareModal({ show: false, success: 0 });
      pollingErrorCenter();
    }
    if (eventInfo?.status !== activityInfo?.status) updateInfo();
  }, [eventInfo?.status, eventInfo?.reedit_status, activityInfo?.status]);

  useEffect(() => {
    if (showPublishDialog) {
      dataReport('e#event_publish#all#exposure');
      updateInfo();
    }
  }, [showPublishDialog]);

  return (
    <>
      <PublishModal
        visible={showPublishDialog}
        sideImg={bgImg}
        img={activityInfo?.cover_url}
        price={lowestPrice}
        status={activityStatus}
        title={activityInfo?.title}
        sponsor={activityInfo?.host_name}
        time={activityTime}
        viewClick={toPreview}
        alertInfos={alertInfo}
        venue={venue}
        tickets={isNoLimit ? undefined : tickets}
        backClick={close}
        sureClick={doPublish}
        participants={participants}
        btns={publishBtn as any}
        className="publish"
        viewActive={() => {
          openPcLobbyPage(eventId, '?isPreview=1');
        }}
        onClose={close}
      />
      <PublishLoading
        visible={loadingVisible}
        onclose={() => setLoadingVisible(false)}
        type={eventInfo?.is_public}
      />

      <ShareActivityModal
        visible={shareModal.show}
        data={activityInfo as any}
        hasFooter={true}
        caption={
          <div className="header-success">
            <img src={COMPLETE_URL} className="complete-img" />
            <span className="text">活动发布成功</span>
          </div>
        }
        userName={activityInfo?.host_name}
        onClose={() => {
          setShareModal({ show: false, success: 0 });
        }}
        publishSuccess={shareModal.success === 1 || shareModal.success === 2}
        reviewSuccess={shareModal.success === 2}
        publishTime={publishedEventInfo?.publish_time}
      />
      <GradientDescDialogTable visible={tableVisible} setVisible={() => setTableVisible(false)} />
    </>
  );
};

Publish.displayName = 'PublishConfirm';

export default memo(Publish);
